import React, { useMemo, useState } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { fromLonLat } from 'ol/proj';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiBookBookmarkThin, PiGearThin } from 'react-icons/pi';
import { EmptyCommand } from '../../../../components/EmptyCommand';
import { ROUTES } from '../../../../routes';
import { useGetBookmarks } from '@ais-ui/websockets';
import { Link } from 'react-router-dom';

export const Bookmarks = () => {
  const { data } = useGetBookmarks();

  const bookmarks = useMemo(() => {
    if (data) {
      return data.sort((a, b) => a.name.localeCompare(b.name));
    }
    return [];
  }, [data]);

  const [open, setOpen] = useState(false);
  const { map } = useOpenLayersContext();

  const onBookmarkSelect = (bookmarkUuid: string) => {
    const bookmark = bookmarks.find((b) => b.uuid === bookmarkUuid);

    if (bookmark) {
      const view = map.getView();

      // Set the center and zoom on the view
      view.setCenter(fromLonLat([bookmark.lon, bookmark.lat]));
      view.setZoom(bookmark.zoomLevel);
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="bookmarks-button"
              size="icon"
              variant="outline"
              className="h-10 w-10 flex-shrink-0 flex-grow-0"
            >
              <PiBookBookmarkThin className="size-5"></PiBookBookmarkThin>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Go to bookmark location</TooltipContent>
      </Tooltip>
      <PopoverContent className="p-0">
        {bookmarks.length === 0 && (
          <div className="p-4">
            <EmptyCommand
              entity="bookmark"
              srcImage="empty_list_bro.avif"
              to={ROUTES.SETTINGS.BOOKMARKS.$path()}
            ></EmptyCommand>
          </div>
        )}
        {bookmarks.length > 0 && (
          <Command>
            <div className="flex items-center justify-between px-3 pt-3 pb-1">
              <h3 className="text-sm font-semibold">Bookmarks</h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={ROUTES.SETTINGS.BOOKMARKS.$path()}
                    className="text-foreground/70 hover:text-foreground transition-colors"
                  >
                    <PiGearThin className="size-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Manage bookmarks</TooltipContent>
              </Tooltip>
            </div>
            <CommandInput placeholder="Find bookmark..." variant="bordered" />
            <CommandList>
              <ScrollArea className="flex-grow overflow-y-auto pr-4 h-60">
                <CommandGroup heading="Zoom to bookmark">
                  {bookmarks.map((bookmark) => (
                    <CommandItem
                      key={bookmark.uuid}
                      value={`${bookmark.name}__${bookmark.uuid}`}
                      onSelect={() => onBookmarkSelect(bookmark.uuid)}
                    >
                      {bookmark.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};
